import { buildVoucherPrintDocument } from './voucherPrintDocument'
import { recordFlightEvent } from '../debug/flightRecorder'

const IPC_TIMEOUT_MS = 60000

async function blobToDataUrl (blob) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('FILE_READER_ERROR'))
    reader.onload = () => resolve(String(reader.result || ''))
    reader.readAsDataURL(blob)
  })
}

async function inlineImagesAsDataUrls (root, baseHref) {
  const imgs = Array.from(root.querySelectorAll('img[src]'))
  const results = []
  for (const img of imgs) {
    const src = img.getAttribute('src') || ''
    if (!src) continue

    // Forzar logo CPagos por IPC (fs) en modo Electron, aunque ya exista un src.
    const looksLikeCpagosLogo =
      src.includes('logo_cpagos') ||
      String(img.getAttribute('alt') || '').toLowerCase().includes('cpagos') ||
      String(img.getAttribute('alt') || '').toLowerCase().includes('centro')
    if (
      looksLikeCpagosLogo &&
      typeof window !== 'undefined' &&
      window.electronAPI &&
      typeof window.electronAPI.getLogoCpagosDataUrl === 'function'
    ) {
      try {
        const r = await window.electronAPI.getLogoCpagosDataUrl()
        if (r && r.ok === true && r.dataUrl) {
          img.setAttribute('src', String(r.dataUrl))
          results.push({ src: src, ok: true, via: 'electron_logo_ipc', file: r.file })
          continue
        }
        results.push({ src: src, ok: false, error: `electron_logo_ipc_failed:${r?.code || 'UNKNOWN'}` })
        continue
      } catch (ipcErr) {
        results.push({ src: src, ok: false, error: `electron_logo_ipc_throw:${ipcErr instanceof Error ? ipcErr.message : String(ipcErr)}` })
        continue
      }
    }

    if (src.startsWith('data:')) continue
    let abs = src
    try {
      abs = baseHref ? new URL(src, baseHref).href : src
    } catch {
      abs = src
    }
    try {
      const res = await fetch(abs)
      const blob = await res.blob()
      const dataUrl = await blobToDataUrl(blob)
      if (dataUrl && dataUrl.startsWith('data:')) {
        img.setAttribute('src', dataUrl)
      }
      results.push({ src: abs, ok: true, bytes: blob.size })
    } catch (e) {
      results.push({ src: abs, ok: false, error: e instanceof Error ? e.message : String(e) })
    }
  }
  return results
}

function captureCanvasDataUrls (sourceRoot) {
  const canvases = Array.from(sourceRoot.querySelectorAll('canvas'))
  const results = []
  const dataUrls = []
  for (const c of canvases) {
    try {
      const dataUrl = c.toDataURL('image/png')
      dataUrls.push({
        dataUrl,
        width: c.width || 0,
        height: c.height || 0,
      })
      results.push({ ok: true })
    } catch (e) {
      dataUrls.push(null)
      results.push({ ok: false, error: e instanceof Error ? e.message : String(e) })
    }
  }
  return { results, dataUrls }
}

function replaceCanvasWithCapturedImages (clonedRoot, captured) {
  const canvases = Array.from(clonedRoot.querySelectorAll('canvas'))
  const results = []
  for (let i = 0; i < canvases.length; i++) {
    const c = canvases[i]
    const cap = captured[i]
    if (!cap || !cap.dataUrl) {
      results.push({ ok: false, reason: 'missing_capture' })
      continue
    }
    try {
      const img = document.createElement('img')
      img.setAttribute('src', cap.dataUrl)
      if (cap.width) img.setAttribute('width', String(cap.width))
      if (cap.height) img.setAttribute('height', String(cap.height))
      c.replaceWith(img)
      results.push({ ok: true })
    } catch (e) {
      results.push({ ok: false, error: e instanceof Error ? e.message : String(e) })
    }
  }
  return results
}

function inlineSvgsAsImages (root) {
  const svgs = Array.from(root.querySelectorAll('svg'))
  const results = []
  for (const svg of svgs) {
    try {
      const xml = new XMLSerializer().serializeToString(svg)
      const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`
      const img = document.createElement('img')
      img.setAttribute('src', dataUrl)
      const w = svg.getAttribute('width')
      const h = svg.getAttribute('height')
      if (w) img.setAttribute('width', String(w))
      if (h) img.setAttribute('height', String(h))
      svg.replaceWith(img)
      results.push({ ok: true })
    } catch (e) {
      results.push({ ok: false, error: e instanceof Error ? e.message : String(e) })
    }
  }
  return results
}

/**
 * Clona un elemento y embebe assets (img->base64, canvas->img base64).
 * Esto evita que el logo (voucher) y el QR (tickets) se pierdan al imprimir en una ventana `data:`.
 */
export async function serializeElementForNativePrint (element, options = {}) {
  const baseHref = options.baseHref || (typeof window !== 'undefined' ? window.location.href : '')
  if (!element || typeof element.cloneNode !== 'function') {
    return { ok: false, code: 'INVALID_ELEMENT' }
  }

  // Capturar bitmap real de canvases ANTES de clonar (el clone pierde el dibujo).
  const capturedCanvas = captureCanvasDataUrls(element)

  const clone = element.cloneNode(true)
  // Necesitamos un root en DOM para crear nodos (document).
  const wrap = document.createElement('div')
  wrap.appendChild(clone)

  const svgResults = inlineSvgsAsImages(wrap)
  const canvasResults = replaceCanvasWithCapturedImages(wrap, capturedCanvas.dataUrls)
  const imgResults = await inlineImagesAsDataUrls(wrap, baseHref)

  return {
    ok: true,
    html: wrap.innerHTML,
    debug: {
      canvases: { captured: capturedCanvas.results, replaced: canvasResults },
      svgs: svgResults,
      images: imgResults,
    },
  }
}

export function voucherNativePrintApisAvailable () {
  return (
    typeof window !== 'undefined' &&
    window.electronAPI &&
    typeof window.electronAPI.getPrintConfig === 'function' &&
    typeof window.electronAPI.printHtml === 'function'
  )
}

/**
 * Intenta imprimir el voucher por IPC (Electron). Si no aplica o falla, el caller debe usar react-to-print.
 * @param {string} innerHTML
 * @param {{ baseHref?: string, requestId?: string }} [options]
 * @returns {Promise<{ ok: boolean, code?: string, message?: string }>}
 */
export async function tryNativePrintHtml (innerHTML, options = {}) {
  const baseHrefOpt = options.baseHref || (typeof window !== 'undefined' ? window.location.href : '')
  const typeOpt = options.type === 'ticket' ? 'ticket' : 'voucher'
  const requestId = options.requestId || `${typeOpt}-${Date.now()}`

  if (!voucherNativePrintApisAvailable()) {
    recordFlightEvent('PRINT:IPC_SKIP', { type: typeOpt, code: 'NO_ELECTRON_API', requestId }, 'voucherNativePrint.tryNativePrintHtml')
    return { ok: false, code: 'NO_ELECTRON_API' }
  }

  let config
  try {
    config = await window.electronAPI.getPrintConfig()
  } catch (e) {
    recordFlightEvent('PRINT:IPC_CONFIG_FAIL', {
      type: typeOpt,
      code: 'CONFIG_ERROR',
      requestId,
      message: e instanceof Error ? e.message : String(e),
    }, 'voucherNativePrint.tryNativePrintHtml')
    return { ok: false, code: 'CONFIG_ERROR', message: e instanceof Error ? e.message : String(e) }
  }

  if (!config || !config.useElectronSilentPrint) {
    recordFlightEvent('PRINT:IPC_SKIP', {
      type: typeOpt,
      code: 'DISABLED',
      requestId,
      useElectronSilentPrint: !!(config && config.useElectronSilentPrint),
    }, 'voucherNativePrint.tryNativePrintHtml')
    return { ok: false, code: 'DISABLED', deviceName: config?.printerName || null }
  }

  const deviceName = config.printerName || null
  const baseHref = baseHrefOpt
  const type = typeOpt
  const html = buildVoucherPrintDocument(innerHTML, { baseHref, printKind: type })
  const payload = {
    html,
    type,
    height: type === 'ticket' ? process.env.REACT_APP_PRINT_TICKET_HEIGHT : process.env.REACT_APP_PRINT_VOUCHER_HEIGHT,
    requestId,
    deviceName: deviceName || undefined,
  }

  recordFlightEvent('PRINT:IPC_START', {
    type,
    requestId,
    deviceName: config.printerName || null,
    htmlLen: typeof html === 'string' ? html.length : 0,
    timeoutMs: IPC_TIMEOUT_MS,
  }, 'voucherNativePrint.tryNativePrintHtml')

  const printPromise = window.electronAPI.printHtml(payload)
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => reject(new Error('PRINT_IPC_TIMEOUT')), IPC_TIMEOUT_MS)
  })

  try {
    const result = await Promise.race([printPromise, timeoutPromise])
    if (result && result.ok === true) {
      recordFlightEvent('PRINT:IPC_OK', { type, requestId }, 'voucherNativePrint.tryNativePrintHtml')
      return { ok: true }
    }
    recordFlightEvent('PRINT:IPC_FAIL', {
      type,
      requestId,
      code: result?.code || 'PRINT_REJECTED',
      message: result?.message,
    }, 'voucherNativePrint.tryNativePrintHtml')
    return {
      ok: false,
      code: result?.code || 'PRINT_REJECTED',
      message: result?.message,
      deviceName,
    }
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    const code = msg === 'PRINT_IPC_TIMEOUT' ? 'TIMEOUT' : 'IPC_ERROR'
    recordFlightEvent('PRINT:IPC_FAIL', { type, requestId, code, message: msg }, 'voucherNativePrint.tryNativePrintHtml')
    return {
      ok: false,
      code,
      message: msg,
      deviceName,
    }
  }
}

// Backward compatible alias (voucher).
export async function tryNativeVoucherPrint (innerHTML, options = {}) {
  return tryNativePrintHtml(innerHTML, { ...options, type: 'voucher' })
}
